import type { Tour, Waypoint } from '@/types/tour'

export const PHOTO_TRACK_ENDPOINT_TOLERANCE_MS = 10 * 60 * 1000

export interface MatchedPhotoCoordinates {
  lat: number
  lon: number
  source: 'interpolated'
}

interface CoordinateCandidate {
  lat: number
  lon: number
  kind: 'interpolated' | 'endpoint'
  nearestTimeDistanceMs: number
  segmentSpanMs: number
}

interface TimedCoordinate {
  lat: number
  lon: number
  time: number
}

function normalizeTimestamp(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.length > 0) {
    const numericValue = Number(value)
    if (Number.isFinite(numericValue)) {
      return numericValue
    }

    const parsedValue = new Date(value).getTime()
    if (Number.isFinite(parsedValue)) {
      return parsedValue
    }
  }

  return null
}

function toTimedCoordinate(point: Waypoint): TimedCoordinate | null {
  const time = normalizeTimestamp((point as Waypoint & { time?: unknown }).time)
  if (time === null) {
    return null
  }

  return Number.isFinite(point.lat) && Number.isFinite(point.lon)
    ? {
        lat: point.lat,
        lon: point.lon,
        time,
      }
    : null
}

function interpolateCoordinate(start: number, end: number, ratio: number): number {
  return start + (end - start) * ratio
}

function selectBetterCandidate(
  currentBest: CoordinateCandidate | null,
  candidate: CoordinateCandidate,
): CoordinateCandidate {
  if (!currentBest) {
    return candidate
  }

  if (currentBest.kind !== candidate.kind) {
    const priority: Record<CoordinateCandidate['kind'], number> = {
      interpolated: 2,
      endpoint: 1,
    }
    return priority[candidate.kind] > priority[currentBest.kind] ? candidate : currentBest
  }

  if (candidate.nearestTimeDistanceMs !== currentBest.nearestTimeDistanceMs) {
    return candidate.nearestTimeDistanceMs < currentBest.nearestTimeDistanceMs
      ? candidate
      : currentBest
  }

  if (candidate.segmentSpanMs !== currentBest.segmentSpanMs) {
    return candidate.segmentSpanMs < currentBest.segmentSpanMs ? candidate : currentBest
  }

  return currentBest
}

export function matchPhotoCoordinatesFromTracks(
  tours: Pick<Tour, 'tracks'>[],
  takenAt: number | null | undefined,
  endpointToleranceMs: number = PHOTO_TRACK_ENDPOINT_TOLERANCE_MS,
): MatchedPhotoCoordinates | null {
  if (typeof takenAt !== 'number' || !Number.isFinite(takenAt)) {
    return null
  }

  let bestCandidate: CoordinateCandidate | null = null

  for (const tour of tours) {
    for (const track of tour.tracks) {
      for (const segment of track.segments) {
        const timedPoints = segment.points
          .map((point) => toTimedCoordinate(point))
          .filter((point): point is TimedCoordinate => point !== null)

        for (const point of timedPoints) {
          const distanceMs = Math.abs(takenAt - point.time)
          if (distanceMs > endpointToleranceMs) {
            continue
          }

          bestCandidate = selectBetterCandidate(bestCandidate, {
            lat: point.lat,
            lon: point.lon,
            kind: 'endpoint',
            nearestTimeDistanceMs: distanceMs,
            segmentSpanMs: 0,
          })
        }

        for (let index = 0; index < timedPoints.length - 1; index += 1) {
          const start = timedPoints[index]
          const end = timedPoints[index + 1]
          const segmentStartTime = Math.min(start.time, end.time)
          const segmentEndTime = Math.max(start.time, end.time)

          if (takenAt < segmentStartTime || takenAt > segmentEndTime) {
            continue
          }

          const timeDelta = end.time - start.time
          const segmentSpanMs = Math.abs(timeDelta)
          const nearestTimeDistanceMs = Math.min(
            Math.abs(takenAt - start.time),
            Math.abs(takenAt - end.time),
          )

          if (timeDelta === 0) {
            bestCandidate = selectBetterCandidate(bestCandidate, {
              lat: start.lat,
              lon: start.lon,
              kind: 'interpolated',
              nearestTimeDistanceMs,
              segmentSpanMs,
            })
            continue
          }

          const ratio = (takenAt - start.time) / timeDelta
          bestCandidate = selectBetterCandidate(bestCandidate, {
            lat: interpolateCoordinate(start.lat, end.lat, ratio),
            lon: interpolateCoordinate(start.lon, end.lon, ratio),
            kind: 'interpolated',
            nearestTimeDistanceMs,
            segmentSpanMs,
          })
        }
      }
    }
  }

  if (!bestCandidate) {
    return null
  }

  return {
    lat: bestCandidate.lat,
    lon: bestCandidate.lon,
    source: 'interpolated',
  }
}
